/**
 * useApi / useApiClient hooks
 * Generic async data fetching with loading/error/refetch, plus a memoized
 * ApiClient for mutations. On a 401 the current session is logged out.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ApiClient, { UnauthorizedError } from '../services/api';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

/**
 * A stable ApiClient that always sends the CURRENT auth token.
 *
 * A TOKEN REFRESH IS NOT A NEW CLIENT. This used to be memoised on `token`, so
 * the hourly refresh (AuthContext) handed every screen a new client — and every
 * useApi below took that as "fetch again, with loading on". The lead file
 * answers loading with "Loading lead…" in place of the whole page, so once an
 * hour it was torn down and rebuilt, and anything typed and not yet saved (a
 * rep's call notes) went with it. The token is read through a ref instead: the
 * client stays the same object, and the next request carries the new token.
 */
export function useApiClient(): ApiClient {
  const { token, refreshToken } = useAuth();
  const tokenRef = useRef(token);
  tokenRef.current = token;
  return useMemo(
    () => new ApiClient(API_BASE_URL, () => tokenRef.current, refreshToken),
    [refreshToken]
  );
}

interface UseApiOptions {
  skip?: boolean;
  dependencies?: any[];
}

interface UseApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

export function useApi<T>(
  fetcher: (api: ApiClient) => Promise<T>,
  options: UseApiOptions = {}
): UseApiState<T> & { refetch: () => Promise<void>; refresh: () => Promise<void> } {
  const { logout } = useAuth();
  const api = useApiClient();
  const [state, setState] = useState<UseApiState<T>>({
    data: null,
    loading: !options.skip,
    error: null,
  });

  const depKey = options.dependencies ? JSON.stringify(options.dependencies) : '';
  // LAST REQUEST WINS. A slow response that lands after a newer one (two quiet
  // refreshes after two quick saves) must not put the older data back.
  const seq = useRef(0);

  const run = useCallback(async () => {
    if (options.skip) return;

    const mine = ++seq.current;
    setState((s) => ({ ...s, loading: true, error: null }));

    try {
      const result = await fetcher(api);
      if (mine !== seq.current) return;
      setState({ data: result, loading: false, error: null });
    } catch (err: any) {
      if (mine !== seq.current) return;
      if (err instanceof UnauthorizedError) {
        logout();
        return;
      }
      setState({ data: null, loading: false, error: err?.message || 'Request failed' });
    }
    // fetcher is intentionally excluded — callers pass inline closures. The
    // token is too: a refreshed token is the same data (see useApiClient).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, options.skip, depKey]);

  useEffect(() => {
    run();
  }, [run]);

  /**
   * refetch() without the loading state. refetch() flips `loading` on, and a
   * panel that renders "Loading…" while loading blanks itself for every edit —
   * the estimate did exactly that. refresh() keeps the current data on screen
   * until the new data replaces it, and on failure keeps it and THROWS, so the
   * caller can say the refresh failed instead of showing an empty panel.
   */
  const refresh = useCallback(async () => {
    if (options.skip) return;
    const mine = ++seq.current;
    try {
      const result = await fetcher(api);
      if (mine !== seq.current) return;
      setState({ data: result, loading: false, error: null });
    } catch (err: any) {
      if (mine !== seq.current) return;
      if (err instanceof UnauthorizedError) { logout(); return; }
      throw err;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, options.skip, depKey]);

  return { ...state, refetch: run, refresh };
}
